import { ProductIdentity } from "./data/collections";

// Helper types
export type Unpacked<T> = T extends Array<infer U> ? U : T extends ReadonlyArray<infer U> ? U : T;
export type ArrayElement<ArrayType extends readonly unknown[]> = ArrayType extends readonly (infer ElementType)[]
  ? ElementType
  : never;

// Maps to the game field in the s3
export enum ProductGame {
  WildRift = "wr",
  LeagueOfLegends = "lol",
  LegendsOfRuneterra = "lor",
  Valorant = "val",
  // TeamfightTactics = "tft",
}

export const PrimaryColorCodeByProductGame: Record<ProductGame, string> = {
  [ProductGame.LeagueOfLegends]: "lol.primary",
  [ProductGame.Valorant]: "valorant.primary",
  [ProductGame.WildRift]: "wildrift.primary",
  [ProductGame.LegendsOfRuneterra]: "lor.primary",
};

export const ProductGameLong = {
  [ProductGame.WildRift]: "wildrift",
  [ProductGame.LeagueOfLegends]: "leagueoflegends",
  [ProductGame.Valorant]: "valorant",
  [ProductGame.LegendsOfRuneterra]: "legendsofruneterra",
  // [ProductGame.TeamfightTactics]: "teamfighttactics",
};

export enum AssetType {
  ICON = "ICON",
  FULL = "FULL",
  SPLASH = "SPLASH",
  LARGE = "LARGE",
  SMALL = "SMALL",
}

export enum ValorantProductType {
  BUNDLE = "BUNDLE",
  PLAYER_CARD = "PLAYER_CARD",
  SPRAY = "SPRAY",
  CHARM = "CHARM",
  WEAPON_SKIN = "WEAPON_SKIN",
}

// Product types to help understand what type of page we're building
export enum ProductType {
  CHAMPION = "CHAMPION",
  CURRENCY = "CURRENCY",
  CHAMPION_SKIN = "CHAMPION_SKIN",
  WR_CHAMPION = "WR_CHAMPION",
  WR_CHAMPION_SKIN = "WR_CHAMPION_SKIN",
  WR_ACCESSORY = "WR_ACCESSORY",
  WR_BUNDLE = "WR_BUNDLE",
  LOL_CHAMPION = "LOL_CHAMPION",
  LOL_CHAMPION_SKIN = "LOL_CHAMPION_SKIN",
  LOL_BUNDLE = "LOL_BUNDLE",
  LOL_VC_BUNDLE = "LOL_VC_BUNDLE",
  LOR_VC_BUNDLE = "LOR_VC_BUNDLE",
  BUNDLE = "BUNDLE",
  PLAYER_CARD = "PLAYER_CARD",
  SPRAY = "SPRAY",
  CHARM = "CHARM",
  WEAPON_SKIN = "WEAPON_SKIN",
}

// Types for the products in the games
export type ChampionProductType = ProductType.WR_CHAMPION | ProductType.LOL_CHAMPION;
export type ChampionSkinProductType = ProductType.WR_CHAMPION_SKIN | ProductType.LOL_CHAMPION_SKIN;
export type BundleProductType = ProductType.WR_BUNDLE | ProductType.LOL_BUNDLE;
export type AccessoryProductType = ProductType.WR_ACCESSORY;
export type WRProducts =
  | ProductType.WR_CHAMPION
  | ProductType.WR_CHAMPION_SKIN
  | ProductType.WR_ACCESSORY
  | ProductType.WR_BUNDLE;
export type LOLProducts = ProductType.LOL_CHAMPION | ProductType.LOL_CHAMPION_SKIN | ProductType.LOL_BUNDLE;

export type ProductGroup = {
  short_name: ProductGame;
  long_name: string;
};

// Page names which are corresponds to an localisation key
export enum PageName {
  SUCCESS = "success",
  NOT_AVAILABLE = "not_available", // 404
  SOURCE_URL_LOST = "source_url_lost", // 421
}

// Modal types which used to handle errors from the DCON and Payments APIs
export enum ModalTypes {
  UNAUTHORIZED = "unauthorized",
  PAYMENT_TIMED_OUT = "payment_timed_out",
  PURCHASE_FAILED = "purchase_failed",
  UNSUPPORTED_REGION = "unsupported_region",
  SERVER_ERROR = "server_error",
  PRODUCT_PURCHASE = "product_purchase",
  ITEM_LISTING = "item_listing",
  KR_PAYMENT_REQUIRED = "kr_payment_required",
}

export type HermesError = {
  httpStatus: number;
  errorCode: ApiErrorTypes;
  message: string;
  implementationDetails: string;
};

// Used for handling the Payments API edge case process which only occurs in a single FoP & country
export enum TransactionStatus {
  COMPLETE = "COMPLETE",
  PENDING = "PENDING",
}

export type Balance = {
  currencyType: string;
  amount: number;
};

export type PurchaseResponse = {
  redirect_url: string;
  error_code?: string;
};

export type ErrorResponse = {
  httpStatus: number;
  errorCode: string;
  message: string;
};

// https://gh.riotgames.com/psa/directbuy-api/blob/2f90cfd00f4e07e74785899225b7b4d1072ffc8a/src/main/java/com/riot-private/directbuyapi/api/dto/ErrorCode.java#L5
export enum ApiErrorTypes {
  NOT_AVAILABLE = "NOT_AVAILABLE",
  NOT_PURCHASEABLE = "NOT_PURCHASEABLE",
  ALREADY_OWNED = "ALREADY_OWNED",
  UNSUPPORTED_REGION = "UNSUPPORTED_REGION",
  REQUIREMENTS_NOT_MET = "REQUIREMENTS_NOT_MET",
  PRODUCT_UNAVAILABLE = "PRODUCT_UNAVAILABLE",
  GAME_ACCOUNT_NOT_FOUND = "GAME_ACCOUNT_NOT_FOUND",
}

/*
 * Data types which is used in collection pages (creator and riot)
 */

export enum PurchaseStatus {
  ORDER_CONFIRMATION_REQUIRED = "ORDER_CONFIRMATION_REQUIRED",
  PAYMENT_REQUIRED = "PAYMENT_REQUIRED",
  EXTERNAL_PAYMENT_REQUIRED = "EXTERNAL_PAYMENT_REQUIRED",
}

// Same with the DCON API:
// https://gh.riotgames.com/psa/directbuy-api/blob/461cd4c0766482d2637451238efeabca3e729ae2/src/main/java/com/riot-private/directbuyapi/gamestore/PurchasabilityStatus.java
export enum PurchasabilityStatus {
  PURCHASABLE = "PURCHASABLE",
  ALREADY_OWNED = "ALREADY_OWNED",
  UNSUPPORTED_REGION = "UNSUPPORTED_REGION",
  REQUIREMENTS_NOT_MET = "REQUIREMENTS_NOT_MET",
  PRODUCT_UNAVAILABLE = "PRODUCT_UNAVAILABLE",
}

export type CollectionType = {
  [key: string]: {
    url: string;
    start?: string;
    end?: string;
    rotations: Array<CollectionRotationType>;
  };
};

export type CollectionRotationType = {
  bannerUrl?: string;
  logoUrl?: string;
  start?: string;
  end?: string;
  sections: Array<CollectionSectionType>;
};

export type CollectionSectionType = {
  type: string;
  headerKey: string;
  order: number;
  sectionName: string;
  products: Array<ProductIdentity>;
};
